import { useState, useEffect, useCallback, useRef } from 'react';
import { useScrollFadeIn } from '../../../hooks/useScrollFadeIn';

const images = [
  { src: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/c935084c9b5b609dc528175763d28f2c.jpeg' },
  { src: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/53925a322e7b179cab2b0dda202f537f.jpeg' },
  { src: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/ee5e1e7e6095198073aec2be2c1d3e4d.jpeg' },
  { src: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/74aad677dadf36c67e0637f87dc166eb.jpeg' },
  { src: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/6efef38d332d1226a60446e77353af25.jpeg' },
  { src: 'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/3d186889e4dcc24d7c64a0e9eb51b7e8.jpeg' },
];

export default function Gallery() {
  const { ref: fadeRef, isVisible } = useScrollFadeIn({ delay: 100 });
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // スワイプ検出用（ライトボックス）
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  // カルーセル用スワイプ
  const carouselTouchStartX = useRef<number | null>(null);
  const carouselTouchStartY = useRef<number | null>(null);
  const isDragging = useRef(false);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev - 1 + images.length) % images.length : null));
  }, []);

  const goNext = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev + 1) % images.length : null));
  }, []);

  const carouselPrev = useCallback(() => {
    setCarouselIndex((prev) => (prev - 1 + images.length) % images.length);
  }, []);

  const carouselNext = useCallback(() => {
    setCarouselIndex((prev) => (prev + 1) % images.length);
  }, []);

  // ライトボックス用タッチ
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
      if (deltaX < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // カルーセル用タッチ
  const handleCarouselTouchStart = (e: React.TouchEvent) => {
    carouselTouchStartX.current = e.touches[0].clientX;
    carouselTouchStartY.current = e.touches[0].clientY;
    isDragging.current = false;
  };

  const handleCarouselTouchMove = (e: React.TouchEvent) => {
    if (carouselTouchStartX.current === null || carouselTouchStartY.current === null) return;
    const deltaX = Math.abs(e.touches[0].clientX - carouselTouchStartX.current);
    const deltaY = Math.abs(e.touches[0].clientY - carouselTouchStartY.current);
    if (deltaX > deltaY && deltaX > 10) {
      isDragging.current = true;
      e.preventDefault();
    }
  };

  const handleCarouselTouchEnd = (e: React.TouchEvent) => {
    if (carouselTouchStartX.current === null || carouselTouchStartY.current === null) return;
    const deltaX = e.changedTouches[0].clientX - carouselTouchStartX.current;
    const deltaY = e.changedTouches[0].clientY - carouselTouchStartY.current;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
      if (deltaX < 0) carouselNext();
      else carouselPrev();
    }
    carouselTouchStartX.current = null;
    carouselTouchStartY.current = null;
    isDragging.current = false;
  };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, goPrev, goNext]);

  return (
    <section
      ref={fadeRef}
      className={`py-16 md:py-24 px-5 md:px-10 bg-[#f5f3f0] transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="max-w-6xl mx-auto">

        {/* ヘッダー */}
        <div className="mb-8 md:mb-14">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 font-medium mb-3">
            Product Gallery
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 leading-snug tracking-tight">
            細部まで、妥協しない。
          </h2>
        </div>

        {/* ── スマートフォン：カルーセル ── */}
        <div className="block md:hidden">
          <div
            className="relative overflow-hidden rounded-xl bg-white shadow-sm select-none"
            onTouchStart={handleCarouselTouchStart}
            onTouchMove={handleCarouselTouchMove}
            onTouchEnd={handleCarouselTouchEnd}
            onClick={() => !isDragging.current && openLightbox(carouselIndex)}
          >
            {/* スライドトラック */}
            <div
              className="flex transition-transform duration-400 ease-out"
              style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
            >
              {images.map((img, i) => (
                <div key={i} className="w-full flex-shrink-0">
                  <div className="w-full aspect-square bg-white flex items-center justify-center p-4">
                    <img
                      src={img.src}
                      alt={`gallery-${i + 1}`}
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* 拡大アイコン */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-black/20 backdrop-blur-sm border border-white/40">
                <i className="ri-zoom-in-line text-white text-lg"></i>
              </div>
            </div>

            {/* 番号バッジ */}
            <div className="absolute top-3 left-3 w-7 h-7 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-gray-900 text-xs font-bold shadow-sm">
              {String(carouselIndex + 1).padStart(2, '0')}
            </div>

            {/* 左右矢印 */}
            <button
              onClick={(e) => { e.stopPropagation(); carouselPrev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/70 backdrop-blur-sm text-gray-800 shadow-sm cursor-pointer active:scale-95 transition-transform"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); carouselNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/70 backdrop-blur-sm text-gray-800 shadow-sm cursor-pointer active:scale-95 transition-transform"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>

          {/* ドットインジケーター */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCarouselIndex(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center ${
                  i === carouselIndex
                    ? 'w-6 h-2 bg-gray-900'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-500'
                }`}
              >
                <span className={`rounded-full ${i === carouselIndex ? 'w-6 h-2 bg-gray-900' : 'w-2 h-2 bg-gray-300'}`}></span>
              </button>
            ))}
          </div>

          {/* スワイプヒント */}
          <p className="text-center text-gray-400 text-[10px] tracking-widest mt-2">
            ← スワイプで切り替え →
          </p>
        </div>

        {/* ── PC：グリッド ── */}
        <div className="hidden md:grid grid-cols-3 gap-5">
          {images.map((img, i) => (
            <div
              key={i}
              onClick={() => openLightbox(i)}
              className="group relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer"
            >
              <div className="w-full aspect-square flex items-center justify-center p-6 bg-white">
                <img
                  src={img.src}
                  alt={`gallery-${i + 1}`}
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-black/20 backdrop-blur-sm border border-white/40">
                  <i className="ri-zoom-in-line text-white text-xl"></i>
                </div>
              </div>
              <div className="absolute top-4 left-4 w-7 h-7 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-bold shadow-sm">
                {String(i + 1).padStart(2, '0')}
              </div>
            </div>
          ))}
        </div>

        {/* 下部テキスト */}
        <div className="mt-10 md:mt-12 flex flex-col items-center gap-3">
          <a
            href="https://mever.design/products/mever-chair-pro-ver2"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full md:max-w-md mx-auto px-8 md:px-12 py-5 md:py-6 bg-purple-600 hover:bg-purple-700 text-white text-lg md:text-2xl font-bold rounded-full transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105 active:scale-[0.97] whitespace-nowrap cursor-pointer text-center block min-h-[56px] flex items-center justify-center"
          >
            今すぐ予約する
          </a>
          <div className="flex items-center gap-2 mt-1 flex-wrap justify-center">
            <span className="text-xs text-gray-400">先行予約限定 30%OFF</span>
            <span className="text-gray-300">／</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-900 text-white text-xs font-bold tracking-widest rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
              各色50脚限定
            </span>
            <span className="text-gray-300">／</span>
            <span className="text-xs text-gray-400">ブラック・グレー</span>
          </div>

          {/* 30日間返品保証バッジ */}
          <div className="mt-3 w-full md:max-w-md mx-auto flex items-center gap-3 px-5 md:px-6 py-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-fill text-2xl md:text-3xl text-emerald-500"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-bold text-emerald-700">30日間返品保証</p>
              <p className="text-xs text-emerald-600 leading-relaxed mt-0.5">
                到着後30日以内なら、理由を問わず返品可能。<br />※商品状態により返金額が異なります（詳細はFAQへ）
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* ライトボックス */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="relative flex flex-col items-center max-w-4xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 cursor-pointer active:scale-95"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="w-full rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={images[lightboxIndex].src}
                alt={`gallery-${lightboxIndex + 1}`}
                className="w-full max-h-[75vh] object-contain bg-white"
              />
            </div>
            <p className="mt-3 text-white/40 text-xs tracking-widest md:hidden">
              ← スワイプで切り替え →
            </p>
            <div className="mt-3 md:mt-5 text-center">
              {/* キャプション削除 */}
            </div>
            <div className="flex items-center gap-6 mt-6">
              <button
                onClick={goPrev}
                className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 cursor-pointer active:scale-95"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <div className="flex items-center gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxIndex(i)}
                    className={`rounded-full transition-all duration-300 cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center ${
                      i === lightboxIndex
                        ? 'w-7 h-2.5 bg-white'
                        : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'
                    }`}
                  >
                    <span className={`rounded-full ${i === lightboxIndex ? 'w-7 h-2.5 bg-white' : 'w-2.5 h-2.5 bg-white/40'}`}></span>
                  </button>
                ))}
              </div>
              <button
                onClick={goNext}
                className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors duration-200 cursor-pointer active:scale-95"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}